import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ErrorMessage from '../common/ErrorMessage';
import LoadingSpinner from '../common/LoadingSpinner';
import OAuthButton from './OAuthButton';
import { getErrorMessage } from '../../services/errors';
import { AuthAPI } from '../../services/api';

interface LoginFormProps {
  onSuccess?: () => void;
  redirectTo?: string;
}

const LoginForm: React.FC<LoginFormProps> = ({
  onSuccess,
  redirectTo = '/dashboard'
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [emailUnverified, setEmailUnverified] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  const { login, oauthLogin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetSuccess = searchParams.get('reset') === 'success';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setEmailUnverified(false);
    setResendSent(false);
    setLoading(true);
    try {
      await login(email, password, rememberMe);
      if (onSuccess) onSuccess();
      else navigate(redirectTo);
    } catch (err: any) {
      const raw: string = err?.response?.data?.message || err?.response?.data?.error || err?.message || '';
      if (/verify.*email.*before|email.*not.*verified/i.test(raw)) {
        setEmailUnverified(true);
      } else {
        setError(getErrorMessage(err, 'Login failed. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    try {
      await AuthAPI.resendVerification(email);
      setResendSent(true);
    } catch {
      // still show sent — avoids enumeration leakage to the user
      setResendSent(true);
    } finally {
      setResendLoading(false);
    }
  };

  const handleOAuthLogin = async (provider: string, token: string, extras?: { redirectUri?: string; codeVerifier?: string }) => {
    setError('');
    setLoading(true);
    try {
      await oauthLogin(provider, token, extras, rememberMe);
      if (onSuccess) onSuccess();
      else navigate(redirectTo);
    } catch (err: any) {
      setError(getErrorMessage(err, `${provider} login failed.`));
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  const isFormValid = email && password && validateEmail(email);

  return (
    <div className="auth-card">
      <div className="auth-card-header">
        <img src="/logo.png" alt="KeeDocs" className="auth-logo" />
        <h2>Welcome back</h2>
        <p>Sign in to your KeeDocs account</p>
      </div>

      <div className="auth-card-body">
        {resetSuccess && (
          <div className="alert alert-success">✓ Password reset successfully. Please sign in with your new password.</div>
        )}
        {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}
        {emailUnverified && (
          <div className="alert alert-warning" style={{ marginBottom: 16 }}>
            {resendSent ? (
              <span style={{ color: 'var(--color-success, #15803d)', fontWeight: 500 }}>
                ✓ Verification email sent — check your inbox.
              </span>
            ) : (
              <span>
                Please verify your email before signing in.{' '}
                <button
                  type="button"
                  className="btn btn-link"
                  style={{ padding: 0, fontSize: 'inherit', fontWeight: 600, verticalAlign: 'baseline' }}
                  onClick={handleResendVerification}
                  disabled={resendLoading}
                >
                  {resendLoading ? 'Sending…' : 'Resend verification email'}
                </button>
              </span>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email" className="form-label">Email address</label>
            <input
              type="email"
              id="email"
              className={`form-control ${email && !validateEmail(email) ? 'is-invalid' : ''}`}
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              disabled={loading}
              placeholder="you@example.com"
              autoComplete="email"
            />
            {email && !validateEmail(email) && (
              <div className="invalid-feedback">Please enter a valid email address</div>
            )}
          </div>

          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <label htmlFor="password" className="form-label" style={{ margin: 0 }}>Password</label>
              <Link to="/forgot-password" style={{ fontSize: 13, color: 'var(--color-primary)' }}>
                Forgot password?
              </Link>
            </div>
            <input
              type="password"
              id="password"
              className="form-control"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              disabled={loading}
              placeholder="••••••••"
              autoComplete="current-password"
            />
          </div>

          <div className="form-group">
            <div className="form-check">
              <input
                type="checkbox"
                id="rememberMe"
                className="form-check-input"
                checked={rememberMe}
                onChange={e => setRememberMe(e.target.checked)}
                disabled={loading}
              />
              <label htmlFor="rememberMe" className="form-check-label">Remember me</label>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary w-100"
            disabled={loading || !isFormValid}
            style={{ marginBottom: 4 }}
          >
            {loading ? <LoadingSpinner size="small" inline /> : 'Sign In'}
          </button>
        </form>

        <div className="auth-divider">or continue with</div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <OAuthButton provider="gmail" onOAuthLogin={handleOAuthLogin} disabled={loading} mode="login" />
          <OAuthButton provider="linkedin" onOAuthLogin={handleOAuthLogin} disabled={loading} mode="login" />
          <OAuthButton provider="facebook" onOAuthLogin={handleOAuthLogin} disabled={loading} mode="login" />
          <OAuthButton provider="twitter" onOAuthLogin={handleOAuthLogin} disabled={loading} mode="login" />
        </div>

        <div className="auth-footer">
          <p style={{ margin: 0 }}>
            Don't have an account?{' '}
            <Link to="/register">Create one free</Link>
          </p>
          <p style={{ marginTop: 8, marginBottom: 0 }}>
            <Link to="/" style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>
              Share a document without signing up →
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
